import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Authenticate } from '../models/server/authenticate';
import { NotificationService } from '../services/actions/notification.service';
import { ConnectedUsersStateService } from '../services/state/connected-users-state.service';
import { HttpError } from './http-error';

/**
 * Utilisé pour intercepter les requêtes HTTP afin de les enrichir et de gérer les reponses d'erreur.
 */
@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
    constructor(
        private connectedUserStateService: ConnectedUsersStateService,
        private router: Router,
        private notificationService: NotificationService
    ) {}

    /**
     * Intercepte les requetes
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const user: Authenticate = this.connectedUserStateService.getUserConnected();
        let skipErrorNotification = false;

        if (req.headers.get('skipErrorNotification')) {
            skipErrorNotification = true;
            req = req.clone({
                headers: req.headers.delete('skipErrorNotification'),
            });
        }

        // Si le header skipAuthentication est présent dans la requête cela indique que l'on ne veut pas ajouter le token d'authentification (ex : dans le cas d'un appel à une API externe)
        if (!req.headers.get('skipAuthentication')) {
            if (!!user) {
                req = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${user.token.accessToken}`,
                    },
                });
            }
        } else {
            // On retire le header skipAuthentication qui peut poser des problèmes avec la CORS Policy du serveur.
            req = req.clone({
                headers: req.headers.delete('skipAuthentication'),
            });
        }
        return next.handle(req).pipe(catchError((error: HttpErrorResponse) => this.handleError(skipErrorNotification, error)));
    }

    /**
     * Gère une réponse en erreur.
     */
    public handleError(skipErrorNotification: boolean, error: HttpErrorResponse) {
        let httpError: HttpError;

        if (error.error instanceof ErrorEvent) {
            // client-side error
            httpError = new HttpError(0, 'Browser error', error.error.message);
        } else {
            if (error.status === 0) {
                httpError = new HttpError(502, 'Server unavailable', 'Le serveur Hub4B ne répond pas; veuillez contacter votre administrateur');
            } else if (error.status === 403) {
                httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error), error.error);
            } else if (error.status === 401 || error.status === 402) {
                if (!error.url.endsWith('api/auth/logon')) {
                    this.connectedUserStateService.setUserConnected(null);
                    this.notificationService.displayError(this.getErrorPhrase(error), this.getErrorMessage(error));
                    this.router.navigate(['/login']);
                }
            } else if (error.status === 409) {
                const json = JSON.parse(error.error);

                if (json.type === 'duplicate') {
                    httpError = new HttpError(error.status, 'Doublon', ` Un élément existe déjà avec une propriété '${json.propertyName}' identique`, error.error);
                } else if (json.type === 'badSettings') {
                    httpError = new HttpError(error.status, 'Paramétrage incorrect', json.message, error.error);
                } else if (json.type === 'alreadyConnected') {
                    httpError = new HttpError(error.status, 'Une autre connexion est active', json.message, error.error);
                } else {
                    httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error), error.error);
                }
            } else if (error.status === 400) {
                if (!error.url.endsWith('api/auth/logon')) {
                    if (!skipErrorNotification) {
                        this.notificationService.displayError(this.getErrorPhrase(error), this.getErrorMessage(error));
                    }
                    httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error));
                }
            } else {
                if (!skipErrorNotification) {
                    this.notificationService.displayError(this.getErrorPhrase(error), this.getErrorMessage(error));
                }
                httpError = new HttpError(error.status, this.getErrorPhrase(error), this.getErrorMessage(error));
            }
        }

        return throwError(httpError);
    }

    /**
     * Retourne un message d'erreur à afficher par l'UI en fonction du code retour de la réponse.
     * @param error Erreur de la réponse.
     */
    private getErrorMessage(error: HttpErrorResponse) {
        switch (error.status) {
            case 404:
                return 'La ressource demandée est introuvable.\r\nveuillez contacter votre administrateur.';
            case 400:
            case 409:
                return error.error;
            case 403:
                return "Vous n'êtes pas autorisé à effectuer cette action.";
            case 401:
            case 402:
                return "Vous n'êtes pas authentifié.\r\nVous allez être redirigé sur la page d'authentification";
            case 503:
                return 'Le service est momentanément indisponible.\r\nVeillez essayer ultérieuremnt.';
            case 500:
                return 'Le serveur a rencontré un problème.\r\nVeuillez contacter votre administrateur.';
            case 501:
                return "L'archivage n'a pas été activé pour votre organisation\r\nVeuillez contacter votre administrateur.";
            case 510:
                return error.error;
            default:
                return 'Veuillez contacter votre administrateur.';
        }
    }

    private getErrorPhrase(error: HttpErrorResponse) {
        switch (error.status) {
            case 404:
                return 'Not Found';
            case 400:
                return 'Bad Request';
            case 409:
                return 'Conflict';
            case 403:
                return 'Forbidden';
            case 401:
            case 402:
                return 'Unauthorized';
            case 503:
                return 'Service Unavailable';
            case 500:
                return 'Internal Server Error';
            case 501:
                return 'Not Implemented';
            case 510:
                return 'Configuration error';
            default:
                return 'Error';
        }
    }
}
